import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Gatsby.css';

/* ── helpers ────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── useReveal ──────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.gats-reveal, .gats-reveal-gold, .gats-reveal-scale';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('gats-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('gats-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── Art Deco ornament SVG divider ─────────────────────────── */
function DecoDivider({ color = '#c9a84c', width = 200 }) {
  return (
    <svg viewBox="0 0 200 20" width={width} height="20" style={{ display: 'block' }}>
      <line x1="0"   y1="10" x2="80"  y2="10" stroke={color} strokeWidth="0.5" />
      <polygon points="90,2 100,10 90,18 80,10"   fill="none" stroke={color} strokeWidth="0.5" />
      <polygon points="110,2 120,10 110,18 100,10" fill="none" stroke={color} strokeWidth="0.5" />
      <line x1="120" y1="10" x2="200" y2="10" stroke={color} strokeWidth="0.5" />
    </svg>
  );
}

/* ── Art Deco horizontal rule with center diamond ──────────── */
function DecoRule({ color = '#c9a84c' }) {
  return (
    <div className="gats-deco-rule">
      <svg viewBox="0 0 320 14" width="100%" height="14">
        <line x1="0"   y1="7" x2="148" y2="7" stroke={color} strokeWidth="0.6" />
        <polygon points="160,0 168,7 160,14 152,7" fill={color} />
        <line x1="172" y1="7" x2="320" y2="7" stroke={color} strokeWidth="0.6" />
      </svg>
    </div>
  );
}

/* ── Art Deco corner frame for hero photo ───────────────────── */
function DecoFrame({ color = '#c9a84c' }) {
  return (
    <svg className="gats-frame-svg" viewBox="0 0 100 100" preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg">
      {/* Corners */}
      <polyline points="0,14 0,0 14,0"    fill="none" stroke={color} strokeWidth="1.2" />
      <polyline points="86,0 100,0 100,14"  fill="none" stroke={color} strokeWidth="1.2" />
      <polyline points="100,86 100,100 86,100" fill="none" stroke={color} strokeWidth="1.2" />
      <polyline points="14,100 0,100 0,86"  fill="none" stroke={color} strokeWidth="1.2" />
      {/* Corner fans (tiny triangles) */}
      <polygon points="0,0 8,0 0,8"   fill={color} opacity="0.5" />
      <polygon points="100,0 92,0 100,8" fill={color} opacity="0.5" />
      <polygon points="100,100 92,100 100,92" fill={color} opacity="0.5" />
      <polygon points="0,100 8,100 0,92" fill={color} opacity="0.5" />
      {/* Mid-side tick marks */}
      <line x1="50" y1="0" x2="50" y2="4"   stroke={color} strokeWidth="0.8" />
      <line x1="50" y1="96" x2="50" y2="100" stroke={color} strokeWidth="0.8" />
      <line x1="0" y1="50" x2="4"  y2="50"  stroke={color} strokeWidth="0.8" />
      <line x1="96" y1="50" x2="100" y2="50" stroke={color} strokeWidth="0.8" />
    </svg>
  );
}

/* ── Countdown ──────────────────────────────────────────────── */
function Countdown({ date, gold }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="gats-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="gats-cu">
          <span className="gats-cu-val" style={{ color: gold }}>{String(v).padStart(2, '0')}</span>
          <span className="gats-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ─────────────────────────────────────────── */
function CalendarWidget({ date, gold, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="gats-calendar">
      <div className="gats-cal-header">
        <span style={{ color: gold }}>{MON[mo]}</span>
        <span className="gats-cal-yr">{yr}</span>
      </div>
      <div className="gats-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="gats-cal-head" style={{ color: gold }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isDay = c.cur && c.n === day;
          return (
            <div key={i}
              className={`gats-cal-cell${!c.cur ? ' gats-cal-muted' : ''}${isDay ? ' gats-cal-today' : ''}`}
              style={isDay ? { background: gold, color: '#080808' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ───────────────────────────────────────────────── */
function RSVPForm({ project, gold }) {
  const [status, setStatus]   = useState('confirmed');
  const [name, setName]       = useState('');
  const [survey, setSurvey]   = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone]       = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="gats-rsvp-done" style={{ borderColor: gold }}>
      <DecoDivider color={gold} />
      <strong style={{ color: gold }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
      <DecoDivider color={gold} />
    </div>
  );

  return (
    <form className="gats-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="gats-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`gats-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { borderColor: gold, color: gold } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="gats-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="gats-survey-q">
          <p className="gats-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="gats-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="gats-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`gats-rsvp-opt gats-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { borderColor: gold, color: gold } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="gats-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`gats-rsvp-opt gats-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { borderColor: gold, color: gold } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p className="gats-field-label">{bc.message.fieldLabel}</p>
          )}
          <textarea className="gats-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="gats-btn" style={{ borderColor: gold, color: gold }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────── */
function Wishlist({ project, gold }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="gats-wishlist">
      {items.map(item => (
        <div key={item.id} className="gats-wish-item">
          <div>
            <div className="gats-wish-name">{item.name}</div>
            {item.price && <div className="gats-wish-price" style={{ color: gold }}>{item.price} ₽</div>}
          </div>
          <div className="gats-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="gats-btn-outline" style={{ borderColor: gold, color: gold }}>
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="gats-btn" style={{ borderColor: '#555', color: '#888' }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="gats-btn" style={{ borderColor: gold, color: gold }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Section card wrapper ────────────────────────────────────── */
function DecoCard({ children, gold }) {
  return (
    <div className="gats-card" style={{ borderColor: gold + '66' }}>
      {/* Art Deco top border fan pattern */}
      <div className="gats-card-top-bar" style={{ borderBottomColor: gold + '66' }}>
        <svg viewBox="0 0 300 12" width="100%" height="12" preserveAspectRatio="none">
          <line x1="0"   y1="6" x2="110" y2="6" stroke={gold} strokeWidth="0.5" opacity="0.7" />
          <polygon points="120,0 130,6 120,12 110,6" fill="none" stroke={gold} strokeWidth="0.6" opacity="0.9" />
          <polygon points="140,0 150,6 140,12 130,6" fill="none" stroke={gold} strokeWidth="0.6" opacity="0.9" />
          <polygon points="160,0 170,6 160,12 150,6" fill="none" stroke={gold} strokeWidth="0.6" opacity="0.9" />
          <line x1="170" y1="6" x2="300" y2="6" stroke={gold} strokeWidth="0.5" opacity="0.7" />
        </svg>
      </div>
      <div className="gats-card-body">
        {children}
      </div>
    </div>
  );
}

/* ── Section title ──────────────────────────────────────────── */
function SectionTitle({ children, gold }) {
  return (
    <div className="gats-section-title-wrap">
      <h2 className="gats-section-title" style={{ color: gold }}>{children}</h2>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN — Gatsby
═══════════════════════════════════════════════════════════════ */
export default function Gatsby({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks  = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc   = project.block_content || {};
  const gold = project.palette?.primary || design?.config?.primaryColor || '#c9a84c';

  const fontTitle = project.fonts?.title || design?.config?.fontTitle || null;
  const fontBody  = project.fonts?.body  || design?.config?.fontBody  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-display': `'${fontTitle}', 'Cinzel Decorative', serif` } : {}),
    ...(fontTitle ? { '--f-title':   `'${fontTitle}', 'Cinzel', serif` }            : {}),
    ...(fontBody  ? { '--f-body':    `'${fontBody}', 'Josefin Sans', sans-serif` }  : {}),
  };

  /* Dynamic Google Fonts */
  useEffect(() => {
    const id = 'gats-base-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700&family=Cinzel:wght@400;600&family=Josefin+Sans:wght@300;400;600&display=swap';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const uid = 'gats-dynamic-fonts';
    let link = document.getElementById(uid);
    if (!link) { link = document.createElement('link'); link.id = uid; link.rel = 'stylesheet'; document.head.appendChild(link); }
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1        = photos[0];
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride  = project.bride || 'Невеста';
  const groom  = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="gats-page" ref={pageRef} style={fontStyle} container-type="inline-size" container-name="gatsby">

      {/* ══ NAV ══════════════════════════════════════════════════ */}
      {HAS_MENU && (
        <nav className="gats-nav" style={{ borderBottomColor: gold + '44' }}>
          <div className="gats-nav-brand" style={{ color: gold }}>
            {bride} &amp; {groom}
          </div>
          <div className="gats-nav-links">
            {HAS_SCHEDULE  && <a href="#schedule"  className="gats-nav-a" style={{ color: gold }}>Тайминг</a>}
            {HAS_MAP       && <a href="#map"       className="gats-nav-a" style={{ color: gold }}>Локация</a>}
            {HAS_DRESSCODE && <a href="#dresscode" className="gats-nav-a" style={{ color: gold }}>Дресс-код</a>}
            {HAS_RSVP      && <a href="#rsvp"      className="gats-nav-a" style={{ color: gold }}>Анкета</a>}
          </div>
        </nav>
      )}

      {/* ══ HERO — dark full-screen ══════════════════════════════ */}
      <section className="gats-hero">
        {/* Spotlight radial gradient */}
        <div className="gats-hero-spotlight" />

        <div className="gats-hero-inner gats-reveal">
          {/* Bride name */}
          <div className="gats-hero-name gats-shimmer" style={{ color: gold }}>{bride}</div>

          {/* Art Deco divider */}
          <div className="gats-hero-divider">
            <DecoDivider color={gold} width={200} />
          </div>

          {/* Photo in gold bordered frame */}
          <div className="gats-hero-photo-wrap">
            <DecoFrame color={gold} />
            {p1
              ? <img className="gats-hero-img" src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
              : <div className="gats-hero-img gats-hero-img-empty" />
            }
          </div>

          {/* Art Deco divider */}
          <div className="gats-hero-divider">
            <DecoDivider color={gold} width={200} />
          </div>

          {/* Groom name */}
          <div className="gats-hero-name gats-shimmer" style={{ color: gold }}>{groom}</div>

          {/* Date */}
          <div className="gats-hero-date" style={{ color: gold }}>{fmtDate(weddingDate)}</div>

          {project.wedding_time && (
            <div className="gats-hero-time" style={{ color: gold + 'bb' }}>{project.wedding_time}</div>
          )}

          {(bc.hero?.subtitle || project.wedding_slogan) && (
            <p className="gats-hero-slogan">{bc.hero?.subtitle || project.wedding_slogan}</p>
          )}

          {HAS_RSVP && (
            <a href="#rsvp" className="gats-btn gats-hero-btn" style={{ borderColor: gold, color: gold }}>
              {bc.rsvp?.yesText || 'Заполнить анкету'}
            </a>
          )}
        </div>
      </section>

      {/* ══ INVITATION ═══════════════════════════════════════════ */}
      <section className="gats-section" id="invitation">
        <DecoRule color={gold} />
        <div className="gats-section-inner gats-inv-grid">
          <div className="gats-reveal">
            <DecoCard gold={gold}>
              <SectionTitle gold={gold}>{bc.message?.title || 'Дорогие наши!'}</SectionTitle>
              <div className="gats-body-text">
                {bc.message?.text
                  ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                  : (
                    <>
                      <p>С огромным волнением и радостью приглашаем вас разделить с нами волшебство нашего торжества.</p>
                      <p>В этот особенный вечер мы будем счастливы видеть вас рядом — в атмосфере роскоши, любви и незабываемых моментов.</p>
                      <p>Ваше присутствие для нас — величайший подарок.</p>
                    </>
                  )
                }
              </div>
              {HAS_RSVP && (
                <a href="#rsvp" className="gats-btn" style={{ borderColor: gold, color: gold, marginTop: 20, display: 'inline-block' }}>
                  {bc.rsvp?.btnText || 'Заполнить анкету'}
                </a>
              )}
            </DecoCard>
          </div>

          <div className="gats-reveal" style={{ '--d': '.15s' }}>
            {/* Invitation photos */}
            <div className="gats-inv-photos">
              {invP1 ? (
                <div className="gats-inv-photo" style={{ borderColor: gold + '55' }}>
                  <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
                </div>
              ) : (
                <div className="gats-inv-photo gats-inv-photo-empty" style={{ borderColor: gold + '55' }}>
                  <span style={{ color: gold + '77' }}>Ваше фото</span>
                </div>
              )}
              {invP2 ? (
                <div className="gats-inv-photo gats-inv-photo--small" style={{ borderColor: gold + '55' }}>
                  <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
                </div>
              ) : (
                <div className="gats-inv-photo gats-inv-photo--small gats-inv-photo-empty" style={{ borderColor: gold + '55' }}>
                  <span style={{ color: gold + '77' }}>Второе фото</span>
                </div>
              )}
            </div>

            <CalendarWidget
              date={weddingDate}
              gold={gold}
              addBtn={HAS_CALENDAR && (() => {
                const d = new Date(weddingDate);
                const pad = n => String(n).padStart(2, '0');
                const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
                const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
                const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                     className="gats-cal-add-btn" style={{ borderColor: gold, color: gold }}>
                    + {bc.calendar?.title || 'Добавить в календарь'}
                  </a>
                );
              })()}
            />
          </div>
        </div>
        <DecoRule color={gold} />
      </section>

      {/* ══ COUNTDOWN ════════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="gats-section" id="countdown">
          <div className="gats-section-inner">
            <DecoCard gold={gold}>
              <SectionTitle gold={gold}>{bc.countdown?.title || 'До торжества'}</SectionTitle>
              <BlockSubtitle text={bc.countdown?.subtitle} className="gats-muted-text" />
              <div className="gats-reveal" style={{ '--d': '.1s' }}>
                <Countdown date={weddingDate} gold={gold} />
              </div>
            </DecoCard>
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ═════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="gats-section" id="schedule">
          <DecoRule color={gold} />
          <div className="gats-section-inner">
            <DecoCard gold={gold}>
              <SectionTitle gold={gold}>{bc.schedule?.title || 'Программа вечера'}</SectionTitle>
              <BlockSubtitle text={bc.schedule?.subtitle} className="gats-muted-text" />
              <div className="gats-schedule-list">
                {optional.length > 0 && (
                  <div className="gats-sched-group">
                    <div className="gats-sched-group-label" style={{ color: gold + 'aa' }}>Необязательная часть</div>
                    {optional.map((item, i) => (
                      <div key={i} className="gats-sched-item gats-reveal" style={{ '--d': `${i * 0.08}s`, borderBottomColor: gold + '22' }}>
                        <div className="gats-sched-time" style={{ color: gold }}>{item.time}</div>
                        <div className="gats-sched-diamond" style={{ background: gold }} />
                        <div className="gats-sched-info">
                          <div className="gats-sched-event">{item.event}</div>
                          {item.description && <div className="gats-sched-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(allItems.length > 0 ? mainItems : [
                  { time: '14:00', event: 'Сбор гостей' },
                  { time: '15:00', event: 'Церемония' },
                  { time: '16:00', event: 'Фотосессия' },
                  { time: '17:00', event: 'Торжественный ужин' },
                ]).map((item, i) => (
                  <div key={i}
                    className={`gats-sched-item gats-reveal${allItems.length === 0 ? ' gats-sched-placeholder' : ''}`}
                    style={{ '--d': `${i * 0.08}s`, borderBottomColor: gold + '22' }}>
                    <div className="gats-sched-time" style={{ color: allItems.length > 0 ? gold : gold + '55' }}>{item.time}</div>
                    <div className="gats-sched-diamond" style={{ background: gold }} />
                    <div className="gats-sched-info">
                      <div className="gats-sched-event">{item.event}</div>
                      {item.description && <div className="gats-sched-desc">{item.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </DecoCard>
          </div>
          <DecoRule color={gold} />
        </section>
      )}

      {/* ══ MAP ══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="gats-section" id="map">
          <div className="gats-section-inner">
            <DecoCard gold={gold}>
              <SectionTitle gold={gold}>{bc.map?.title || 'Место проведения'}</SectionTitle>
              <BlockSubtitle text={bc.map?.subtitle} className="gats-muted-text" />
              <div className="gats-venues gats-reveal" style={{ '--d': '.05s' }}>
                {venues.length > 0 ? venues.map((v, i) => (
                  <div key={i} className="gats-venue-row" style={{ borderColor: gold + '33' }}>
                    <div className="gats-venue-diamond" style={{ background: gold }} />
                    <div className="gats-venue-info">
                      {v.label && <span className="gats-venue-label" style={{ color: gold }}>{v.label} · </span>}
                      {v.name  && <span className="gats-venue-name">{v.name} · </span>}
                      <span>{v.address}</span>
                    </div>
                    <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                      target="_blank" rel="noopener noreferrer" className="gats-btn-outline" style={{ borderColor: gold, color: gold }}>
                      Маршрут
                    </a>
                  </div>
                )) : (
                  project.wedding_address
                    ? <div className="gats-venue-row" style={{ borderColor: gold + '33' }}><span>{project.wedding_address}</span></div>
                    : <div className="gats-muted-text">Адрес будет добавлен на шаге «Данные»</div>
                )}
              </div>
              {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="gats-map-frame gats-reveal" style={{ '--d': '.15s', borderColor: gold + '44' }}>
                {(() => {
                  if (project.map_url) return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                  const withCoords = venues.filter(v => v.lat && v.lon);
                  if (withCoords.length > 0) {
                    const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                    const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                    const pts = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                    const mapSrc = `https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=16&pt=${pts}&lang=ru_RU`;
                    return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                  }
                  const addr = venues.length > 0 ? venues.map(v => v.address).filter(Boolean).join(' — ') : (project.wedding_address || '');
                  if (!addr) return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                  const mapSrc = venues.length > 1
                    ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                    : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                  return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                })()}
              </div>
            )}
            </DecoCard>
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="gats-section" id="dresscode">
          <DecoRule color={gold} />
          <div className="gats-section-inner">
            <DecoCard gold={gold}>
              <SectionTitle gold={gold}>{bc.palette?.title || 'Дресс-код'}</SectionTitle>
              <BlockSubtitle text={bc.palette?.subtitle} className="gats-body-text" tag="div" />
              <div className="gats-swatches gats-reveal" style={{ '--d': '.05s' }}>
                {(project.dresscode_palette || []).map((sw, i) => {
                  const c = sw.color || '#000';
                  const hex = c.replace('#', '');
                  const r = parseInt(hex.slice(0, 2), 16) || 0, g = parseInt(hex.slice(2, 4), 16) || 0, b = parseInt(hex.slice(4, 6), 16) || 0;
                  const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                  return (
                    <div key={i} className="gats-swatch gats-reveal-scale" style={{ background: c, borderColor: gold + '66', '--d': `${i * 0.08}s` }}>
                      <span style={{ color: isLight ? '#080808' : '#f5eed8' }}>{sw.label}</span>
                    </div>
                  );
                })}
              </div>
              {dcPhotos.length > 0 && (
                <div className="gats-dc-photos gats-reveal" style={{ '--d': '.15s' }}>
                  {dcPhotos.map((p, i) => (
                    <div key={i} className="gats-dc-photo" style={{ borderColor: gold + '44' }}>
                      <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                    </div>
                  ))}
                </div>
              )}
            </DecoCard>
          </div>
          <DecoRule color={gold} />
        </section>
      )}

      {/* ══ RSVP / WISHLIST ══════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <section className="gats-section" id="rsvp">
          <div className="gats-section-inner">
            <div className="gats-rsvp-grid">
              {HAS_RSVP && (
                <div className="gats-reveal">
                  <DecoCard gold={gold}>
                    <SectionTitle gold={gold}>{bc.rsvp?.title || 'Анкета'}</SectionTitle>
                    <BlockSubtitle text={bc.rsvp?.subtitle} className="gats-muted-text" />
                    {bc.rsvp?.deadline && (
                      <p className="gats-muted-text" style={{ color: gold }}>
                        Ответьте до: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                      </p>
                    )}
                    <RSVPForm project={project} gold={gold} />
                  </DecoCard>
                </div>
              )}
              {HAS_WISHLIST && (
                <div className="gats-reveal" style={{ '--d': '.1s' }}>
                  <DecoCard gold={gold}>
                    <SectionTitle gold={gold}>{bc.wishlist?.title || 'Список желаний'}</SectionTitle>
                    <BlockSubtitle text={bc.wishlist?.subtitle} className="gats-muted-text" />
                    <Wishlist project={project} gold={gold} />
                  </DecoCard>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ WATERMARK ════════════════════════════════════════════ */}
      {(!project.payment_plan || project.payment_plan === 'free') && (
        <div style={{
          position: 'fixed', bottom: 0, right: 0, zIndex: 9999,
          background: 'rgba(8,8,8,0.9)', color: '#f5eed8',
          padding: '7px 16px', fontSize: '0.78rem',
          borderTopLeftRadius: 6, borderTop: `1px solid ${gold}55`,
          borderLeft: `1px solid ${gold}55`, backdropFilter: 'blur(6px)',
        }}>
          Создано на <strong style={{ color: gold }}>
            <a href="/" style={{ color: gold, textDecoration: 'none' }}>Amorika</a>
          </strong>
        </div>
      )}

      {/* ══ FOOTER ═══════════════════════════════════════════════ */}
      <footer className="gats-footer" style={{ borderTopColor: gold + '44' }}>
        <DecoDivider color={gold} width={240} />
        <div className="gats-footer-names gats-shimmer" style={{ color: gold }}>{bride} &amp; {groom}</div>
        <div className="gats-footer-date" style={{ color: gold + 'aa' }}>{fmtDate(weddingDate)}</div>
        {project.wedding_slogan && (
          <div className="gats-footer-slogan">{project.wedding_slogan}</div>
        )}
        <DecoDivider color={gold} width={160} />
        <div className="gats-footer-brand">
          Создано на <a href="/" style={{ color: gold, textDecoration: 'none' }}><strong>Amorika</strong></a>
        </div>
      </footer>

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
